import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';

const initialState = {};

export const vehicleSliceV2 = createSlice({
	name: 'vehicleStateV2',
	initialState,
	reducers: {},
});

/**
 * Create Vehicle Type V2
 * @param {Object} req_data - Vehicle type data
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 */
export const createVehicleTypeV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			create_type: 'vehicle_type',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Vehicle type created successfully');
			}
			return res;
		});
	};

/**
 * Update Vehicle Type V2
 * @param {Object} req_data - Vehicle type update data (must include id)
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 */
export const updateVehicleTypeV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			update_type: 'vehicle_type',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Vehicle type updated successfully');
			}
			return res;
		});
	};

/**
 * Delete Vehicle Type V2 (Soft Delete)
 * @param {Number} id - Vehicle Type ID
 * @returns {Promise} API response with updated vehicle type
 *
 * Note: This is a soft delete that sets is_active = false
 * Will fail if vehicle type is in use by active vehicles/drivers/routes/contracts
 *
 * Error codes:
 * - NOT_FOUND: Vehicle type ID does not exist
 * - IN_USE: Cannot delete vehicle type that is in use by active entities
 */
export const deleteVehicleTypeV2 =
	({ id }) =>
	async () => {
		const data = {
			update_type: 'vehicle_type',
			data: {
				id,
				is_active: false,
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			version: 'v2',
		};

		try {
			const response = await API.common(CREDENTIALS);
			if (response.status === 'success') {
				toaster.success(response.message || 'Vehicle type deactivated successfully');
				return response;
			}
			if (response.code === 'NOT_FOUND') {
				toaster.error('Vehicle type not found');
				return response;
			}
			if (response.code === 'IN_USE') {
				toaster.error(
					response.message ||
						'Cannot deactivate vehicle type that is currently in use. ' +
							'Please ensure it is not assigned to any active vehicles, drivers, routes, or contracts.'
				);
				return response;
			}
			toaster.error(response.message || 'Failed to deactivate vehicle type');
			return response;
		} catch (error) {
			console.error('Error deleting vehicle type:', error);
			toaster.error('An error occurred while deactivating vehicle type');
			throw error;
		}
	};

/**
 * Search Vehicle Types V2
 * @param {Object} filter_by - Filter criteria
 * @param {Object} pagination - Pagination config { page, per_page }
 * @param {Object} sort_by - Sort config { field, order }
 * @returns {Promise} API response with vehicle types array and pagination
 */
export const searchVehicleTypesV2 =
	({ filter_by = {}, pagination = { page: 1, per_page: 20 }, sort_by = null }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const requestData = {
			search_type: 'vehicle_type',
			filter_by: cleanFilterBy,
			pagination: {
				page: pagination?.page || 1,
				per_page: pagination?.per_page || 20,
			},
		};

		// Add sort_by if provided
		if (sort_by && sort_by.field && sort_by.order) {
			requestData.sort_by = {
				field: sort_by.field,
				order: sort_by.order,
			};
		}

		const CREDENTIALS = {
			url: '/search',
			data: requestData,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// API returns { status: "success", data: { vehicle_types: [...], pagination: {...} } }
			const vehicleTypes = Array.isArray(response?.data?.vehicle_types)
				? response.data.vehicle_types
				: [];
			const paginationData = response?.data?.pagination || {};

			return {
				rows: vehicleTypes,
				pagination: paginationData,
				count: paginationData.total || vehicleTypes.length || 0,
			};
		});
	};

/**
 * Get Vehicle Type by ID V2
 * @param {Number} id - Vehicle Type ID
 * @returns {Promise} Vehicle type object with usage details or null
 */
export const getVehicleTypeByIdV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vehicle_type',
				filter_by: {
					id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// API returns { status: "success", data: { vehicle_type: {...} } }
			if (response?.status === 'success' && response?.data?.vehicle_type) {
				return response.data.vehicle_type;
			}
			return null;
		});
	};

/**
 * List Vehicle Types V2 (for dropdowns/autocomplete)
 * @param {Object} filter_by - Filter criteria (is_active, vehicle_category, etc.)
 * @param {Number} limit - Maximum number of results (min: 1, max: 100, default: 50)
 * @returns {Promise} API response with results array in format: { id, label, value, metadata }
 *
 * Response format:
 * {
 *   results: [
 *     { id, label: "Tata Ace", value: 1, metadata: { maker_manufacturer, vehicle_category, ... } }
 *   ],
 *   total: 2
 * }
 */
export const searchVehicleTypeFiltersV2 =
	({ filter_by = {}, limit = 50 }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		// Validate and clamp limit
		const validLimit = Math.min(Math.max(1, limit || 50), 100);

		const CREDENTIALS = {
			url: '/list_search',
			data: {
				list_search_type: 'vehicle_type',
				filter_by: cleanFilterBy,
				limit: validLimit,
			},
			version: 'v2',
		};

		try {
			const response = await API.common(CREDENTIALS);
			if (response?.status === 'success' && response?.data) {
				return {
					results: response.data.results || [],
					total: response.data.total || 0,
				};
			}
			return { results: [], total: 0 };
		} catch (error) {
			console.error('Error fetching vehicle type list:', error);
			return { results: [], total: 0 };
		}
	};

/**
 * Export Vehicle Types CSV V2
 * @param {Object} filter_by - Filter criteria for export
 * @param {String} format - Export format (default: 'csv')
 * @returns {Promise} API response with download_url
 */
export const exportVehicleTypesCSVV2 =
	({ filter_by = {}, format = 'csv' }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: '/report',
			data: {
				report_type: 'vehicle_type_export',
				format: format || 'csv',
				data: {
					filter_by: cleanFilterBy,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success' && response?.data?.download_url) {
				// Open download URL in new tab
				window.open(response.data.download_url, '_blank');
				toaster.success(
					`Export generated successfully. ${
						response.data.total_records || 0
					} records exported.`
				);
			}
			return response;
		});
	};

/**
 * Sync Vehicle Types V2 (from external system or database)
 * @returns {Promise} API response
 */
export const syncVehicleTypesV2 = () => async () => {
	const CREDENTIALS = {
		url: '/sync',
		data: {
			sync_type: 'vehicle_type',
		},
		version: 'v2',
	};

	return API.common(CREDENTIALS).then((res) => {
		if (res.status === 'success') {
			toaster.success(res.message || 'Vehicle types synced successfully');
		}
		return res;
	});
};

/**
 * Bulk Upload Vehicle Types V2
 * @param {File} file - CSV/Excel file containing vehicle types
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 */
export const bulkUploadVehicleTypesV2 =
	({ file, setErrors }) =>
	async () => {
		const formData = new FormData();
		formData.append('file', file);
		formData.append('upload_type', 'vehicle_type');

		const CREDENTIALS = {
			url: '/bulk_upload',
			data: formData,
			setErrors,
			version: 'v2',
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(
					res.message ||
						`Successfully uploaded ${res.data?.success_count || 0} vehicle types`
				);
			}
			return res;
		});
	};

/**
 * Search Vehicles V2 (Actual Vehicles, not Vehicle Types)
 * @param {Object} filter_by - Filter criteria
 * @param {Object} pagination - Pagination config { page, per_page }
 * @param {Object} sort_by - Sort config { field, order }
 * @returns {Promise} API response with vehicles array and pagination
 */
export const searchVehiclesV2 =
	({ filter_by = {}, pagination = { page: 1, per_page: 20 }, sort_by = null }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const requestData = {
			search_type: 'vehicle',
			filter_by: cleanFilterBy,
			pagination: {
				page: pagination?.page || 1,
				per_page: pagination?.per_page || 20,
			},
		};

		// Add sort_by if provided
		if (sort_by && sort_by.field && sort_by.order) {
			requestData.sort_by = {
				field: sort_by.field,
				order: sort_by.order,
			};
		}

		const CREDENTIALS = {
			url: '/search',
			data: requestData,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// API returns { status: "success", data: { vehicles: [...], pagination: {...} } }
			const vehicles = Array.isArray(response?.data?.vehicles) ? response.data.vehicles : [];
			const paginationData = response?.data?.pagination || {};

			return {
				rows: vehicles,
				pagination: paginationData,
				count: paginationData.total || vehicles.length || 0,
			};
		});
	};

/**
 * Create Vehicle V2
 * @param {Object} req_data - Vehicle data
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 */
export const createVehicleV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			create_type: 'vehicle',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Vehicle created successfully');
			}
			return res;
		});
	};

/**
 * Update Vehicle V2
 * @param {Object} req_data - Vehicle update data (must include id)
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 *
 * Note:
 * - vehicle_number cannot be changed (immutable)
 * - gps_device_id must be unique if changed
 *
 * Error codes:
 * - NOT_FOUND: Vehicle ID does not exist
 * - VALIDATION_ERROR: Invalid data provided
 * - DUPLICATE_GPS_DEVICE: GPS device ID already in use
 */
export const updateVehicleV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			update_type: 'vehicle',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Vehicle updated successfully');
			}
			return res;
		});
	};

/**
 * Transfer Vehicle V2
 * @param {Object} req_data - Transfer data
 * @param {Number} req_data.vehicle_id - Vehicle ID to transfer (required)
 * @param {Number} req_data.to_vendor_id - Target vendor ID (required, must be different from current)
 * @param {String} req_data.reason - Reason for transfer (required, max 255 chars)
 * @param {String} req_data.last_working_date - Last date with previous vendor (required, ISO Date)
 * @param {String} req_data.start_date - Start date with new vendor (required, ISO Date)
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 *
 * Business Rules:
 * - Vehicle must not have active trips
 * - Creates pending approval in approvals_v2 table
 * - Transfer executed only after approval
 * - Transfer history created after approval
 *
 * Response includes approval_id and approval_status='pending'
 *
 * Error codes:
 * - NOT_FOUND: Vehicle or vendor not found
 * - INVALID_STATE: Vehicle has active trips
 * - SAME_VENDOR: to_vendor_id same as current vendor_id
 */
export const transferVehicleV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			update_type: 'transfer_vehicle',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(
					res.message ||
						'Vehicle transfer request created. Transfer will be executed after approval.'
				);
			}
			return res;
		});
	};

/**
 * Replace Vehicle V2
 * Replaces a vehicle with another vehicle within the same vendor.
 *
 * Error codes:
 * - NOT_FOUND: Vehicle or vendor not found
 * - INVALID_STATE: Vehicle has active trips
 * - SAME_VEHICLE: new_vehicle_id same as old_vehicle_id
 */
export const replaceVehicleV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			update_type: 'replace_vehicle',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Vehicle replacement request created successfully.');
			}
			return res;
		});
	};

/**
 * Export Vehicles V2
 * @param {Object} filter_by - Filter criteria for export
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response with download URL
 */
export const exportVehiclesV2 =
	({ filter_by, setErrors }) =>
	async () => {
		const data = {
			report_type: 'vehicle_export',
			format: 'csv',
			data: {
				filter_by: filter_by || {},
			},
		};

		const CREDENTIALS = {
			url: '/report',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Export file generated successfully');
			}
			return res;
		});
	};

/**
 * ============================================================================
 * USAGE EXAMPLES for List Vehicle Types (searchVehicleTypeFiltersV2)
 * ============================================================================
 *
 * This function is designed for dropdown/autocomplete components where you need
 * a simplified list of vehicle types with labels and values.
 *
 * Example 1: Get all active vehicle types
 * ------------------------------------------
 * const response = await dispatch(
 *   searchVehicleTypeFiltersV2({
 *     filter_by: { is_active: true },
 *     limit: 50
 *   })
 * );
 * // Returns: { results: [{id, label, value, metadata}], total }
 *
 * Example 2: Get light category vehicles only
 * --------------------------------------------
 * const response = await dispatch(
 *   searchVehicleTypeFiltersV2({
 *     filter_by: {
 *       is_active: true,
 *       vehicle_category: 'light'
 *     },
 *     limit: 20
 *   })
 * );
 *
 * Example 3: Use in Select component
 * -----------------------------------
 * const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
 *
 * useEffect(() => {
 *   dispatch(searchVehicleTypeFiltersV2({ filter_by: { is_active: true } }))
 *     .then(response => {
 *       setVehicleTypeOptions(response.results);
 *     });
 * }, []);
 *
 * return (
 *   <Select placeholder="Select Vehicle Type">
 *     {vehicleTypeOptions.map(option => (
 *       <Option key={option.value} value={option.value}>
 *         {option.label}
 *       </Option>
 *     ))}
 *   </Select>
 * );
 */

export default vehicleSliceV2.reducer;
